import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, ExternalLink, Play } from "lucide-react";
import { YOUTUBE_ID, FILM_EMBED_ENABLED } from "@/lib/site";

export default function VideoModal({ open, onClose }) {
  const holder = useRef(null);
  const readyRef = useRef(false);
  const playedRef = useRef(false);
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const wantsEmbed = FILM_EMBED_ENABLED || attempt > 0 || open;

  useEffect(() => {
    if (!open || !wantsEmbed) return;
    window.dispatchEvent(new CustomEvent("cr:music-pause"));
    setFailed(false);
    let player = null;
    let cancelled = false;
    let poll = null;

    const start = () => {
      if (cancelled || !holder.current || !window.YT || !window.YT.Player) return;
      readyRef.current = false;
      playedRef.current = false;
      setTimeout(() => {
        if (!cancelled && !playedRef.current) setFailed(true);
      }, 6000);
      // mount in an unmanaged child so YouTube can replace it without breaking React's tree
      const mount = document.createElement("div");
      holder.current.appendChild(mount);
      player = new window.YT.Player(mount, {
        videoId: YOUTUBE_ID,
        width: "100%",
        height: "100%",
        playerVars: { autoplay: 1, rel: 0 },
        events: {
          onReady: (e) => {
            readyRef.current = true;
            try {
              e.target.playVideo();
            } catch (_) {}
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) {
              playedRef.current = true;
            }
          },
          onError: () => setFailed(true),
        },
      });
    };

    if (window.YT && window.YT.Player) {
      start();
    } else {
      if (!document.getElementById("yt-iframe-api")) {
        const s = document.createElement("script");
        s.id = "yt-iframe-api";
        s.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(s);
      }
      poll = setInterval(() => {
        if (window.YT && window.YT.Player) {
          clearInterval(poll);
          start();
        }
      }, 300);
      setTimeout(() => poll && clearInterval(poll), 10000);
    }

    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      try {
        if (player && player.destroy) player.destroy();
      } catch (_) {}
      if (holder.current) holder.current.innerHTML = "";
    };
  }, [open, attempt, wantsEmbed]);

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
      data-testid="hero-video-modal"
    >
      <button
        className="absolute right-5 top-5 z-10 rounded-full border border-white/20 p-3 text-white hover:border-[#f8b818] hover:text-[#f8b818]"
        onClick={onClose}
        aria-label="Close video"
        data-testid="video-modal-close-btn"
      >
        <X size={20} />
      </button>
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="relative aspect-video w-full max-w-4xl overflow-hidden rounded-2xl bg-[#141416]"
        onClick={(e) => e.stopPropagation()}
      >
        {failed ? (
          <div key="video-fallback" className="flex h-full w-full flex-col items-center justify-center gap-4 p-6 text-center" data-testid="video-embed-fallback">
            <div className="relative">
              <img
                src={`https://img.youtube.com/vi/${YOUTUBE_ID}/maxresdefault.jpg`}
                alt="Cool Runnings park film"
                className="w-full max-w-xl rounded-xl opacity-90"
              />
              <button
                onClick={() => {
                  setFailed(false);
                  setAttempt((a) => a + 1);
                }}
                className="group absolute inset-0 flex items-center justify-center rounded-xl"
                aria-label="Play the film here"
                title="Play the film here"
                data-testid="video-retry-play-btn"
              >
                <span className="glass flex h-20 w-20 items-center justify-center rounded-full text-[#f8b818] shadow-[0_10px_40px_rgba(0,0,0,0.5)] transition-transform duration-300 group-hover:scale-110">
                  <Play size={32} className="ml-1 fill-[#f8b818]" />
                </span>
              </button>
            </div>
            <p className="max-w-md text-sm text-[#b9b4a6]" data-testid="video-fallback-note">
              In-page play is switched off for this film on YouTube — one click on 'Allow embedding' in YouTube
              Studio turns it on. Watch it there now:
            </p>
            <a
              href={`https://www.youtube.com/watch?v=${YOUTUBE_ID}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold !px-6 !py-3 !text-sm"
              data-testid="video-fallback-link"
            >
              <Play size={16} /> Watch the film on YouTube <ExternalLink size={14} />
            </a>
          </div>
        ) : (
          <div className="relative h-full w-full">
            <img
              src={`https://img.youtube.com/vi/${YOUTUBE_ID}/maxresdefault.jpg`}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover opacity-40"
            />
            <div ref={holder} className="relative h-full w-full" data-testid="hero-video-iframe" />
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
